import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom"; // Import useNavigate
import { useForm, Controller } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

import { useLocations, useManagerDetail } from "../../hooks/queries";
import { useCreateManager, useUpdateManager } from "../../hooks/mutation";

import HeaderContainer from "../CrewDashboard/HeaderContainer";
import TextInput from "../common/TextInput";
import CustomSelect from "../common/select";

const ManagerNew = () => {
  const navigate = useNavigate();

  const { id: managerID } = useParams();
  const { data: locationsData, isSuccess: successLocations } = useLocations();
  const locationsList = useMemo(() => {
    return successLocations
      ? locationsData.map((_location) => ({
        label: _location.Code ? `${_location.Code} - ${_location.Description}` : _location.Description,
        value: _location.LocationID,
      }))
      : [];
  }, [locationsData, successLocations]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      locationIds: [],
    },
  });
  const { data: managerDetail, isSuccess: successManagerData } =
    useManagerDetail(managerID);

  useEffect(() => {
    if (successManagerData && managerDetail) {
      // Supabase FK-relationship queries return the related row as an array
      // even for a one-to-one link. Unwrap it before reading firstname/lastname
      // (the manager list page does the same). Without this the edit form
      // pre-fills as blank because .firstname on an array is undefined.
      const mgr = Array.isArray(managerDetail.Managers)
        ? managerDetail.Managers[0]
        : managerDetail.Managers;
      reset({
        firstname: mgr?.firstname || "",
        lastname: mgr?.lastname || "",
        email: managerDetail.Email || "",
        password: "",
        locationIds:
          managerDetail.LocationIDs ||
          (managerDetail.LocationID ? [managerDetail.LocationID] : []),
      });
    }
  }, [managerDetail, reset, successManagerData]);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
  }, []);
  const onError = useCallback(
    (err) => {
      reset();
    },
    [reset]
  );
  const { mutate: createManager, isPending: isCreating } = useCreateManager(
    onSuccess,
    onError
  );
  const { mutate: updateManager, isPending: isUpdating } = useUpdateManager(
    onSuccess,
    onError
  );

  const handleSave = useCallback(
    (data) => {
      if (managerID) {
        updateManager({ ...data, UserID: managerID });
      } else {
        createManager(data);
      }
    },
    [createManager, managerID, updateManager]
  );
  const handleBack = useCallback(() => {
    navigate("/manager-detail");
  }, [navigate]);

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">
          {managerID ? "Update Manager" : "New Manager"}
        </p>
        <span>
          {managerID ? "Update manager info" : "Create a new manager"}
        </span>
        <div className="w-100 mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>
              <p>Full Name</p>
            </div>
            <div className="flex w-60 justify-end items-end mobile-col gap-2">
              <Controller
                control={control}
                name="firstname"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg"
                    error={errors.firstname?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="lastname"
                rules={{
                  required: "Required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg"
                    error={errors.lastname?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Email</p>
            </div>
            <div className="flex justify-end w-60">
              <Controller
                control={control}
                name="email"
                rules={{
                  required: "Required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email is not valid",
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.email?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Password</p>
            </div>
            <div className="flex justify-end w-60">
              <Controller
                control={control}
                name="password"
                rules={!managerID ? { required: "Required" } : {}}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="password"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.password?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Assigned Service Locations</p>
            </div>
            <div className="flex w-60 justify-end">
              <Controller
                control={control}
                name="locationIds"
                render={({ field: { value, onChange } }) => (
                  <CustomSelect
                    value={value}
                    onChange={onChange}
                    id="location-select"
                    options={locationsList}
                    isMulti={true}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mt-4 save-button">
            <button
              disabled={isCreating | isUpdating}
              onClick={handleSubmit(handleSave)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{managerID ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={handleBack}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerNew;
